import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

function Empty({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty"
      className={cn(
        'mx-auto flex w-full max-w-96 min-w-0 flex-col items-center justify-center gap-6 self-center rounded-lg py-12 text-center text-foreground',
        className
      )}
      {...props}
    />
  );
}

const emptyMediaVariants = cva(
  'flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Illustrations ship at 2x (272px), so their display size lives here, not at each callsite.
        default: 'bg-transparent [&>img]:size-34',
        icon: "flex size-8 shrink-0 items-center justify-center rounded-lg border border-stroke bg-linear-to-b from-bg-muted-light to-bg-muted p-2 text-foreground-muted [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

function EmptyMedia({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof emptyMediaVariants>) {
  return (
    <div
      data-slot="empty-media"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  );
}

function EmptyHeader({ className, children, ...props }: React.ComponentProps<'div'>) {
  const childrenArray = React.Children.toArray(children);
  const [firstChild, ...restChildren] = childrenArray;
  const isFirstChildMedia = React.isValidElement(firstChild) && firstChild.type === EmptyMedia;
  const media = isFirstChildMedia ? firstChild : null;
  const rest = isFirstChildMedia ? restChildren : childrenArray;
  const mediaVariant =
    React.isValidElement<VariantProps<typeof emptyMediaVariants>>(media) &&
    media.props.variant === 'icon'
      ? 'icon'
      : 'default';

  return (
    <div
      data-slot="empty-header"
      data-media-variant={mediaVariant}
      className={cn(
        'group/header flex max-w-sm flex-col items-center gap-2 text-center',
        mediaVariant === 'icon' && 'gap-4',
        className
      )}
      {...props}
    >
      {media}
      <div className={cn('flex flex-col items-center gap-1.5', mediaVariant === 'icon' && 'gap-2')}>
        {rest}
      </div>
    </div>
  );
}

function EmptyTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-title"
      className={cn(
        'text-lg font-medium group-data-[media-variant=icon]/header:text-base group-data-[media-variant=icon]/header:leading-6',
        className
      )}
      {...props}
    />
  );
}

function EmptyDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="empty-description"
      className={cn(
        'text-sm text-foreground-muted [&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-informational',
        className
      )}
      {...props}
    />
  );
}

function EmptyContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-content"
      className={cn(
        'flex w-full max-w-sm min-w-0 flex-col items-center gap-6 text-sm text-balance',
        className
      )}
      {...props}
    />
  );
}

export { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent, EmptyMedia };
